/**
 * @summary - Component that Provides PredictiveMaintenance Functionality
 * @author vengateshwaran k
 * @since 2024-07-28
 */
import {Box,Button,Grid,Stack} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import commonMasterRepository from '../../api/repositories/commonMasterRepository';
import PremitiveMaintenanceRepository from "../../api/repositories/premitiveMaintenanceRepository";
import { DefaultCard } from "../../assets/styled-components/card";
import {getAuthToken, getFacilityId,getcompanyId} from "../../utility/tokenUtility";
import CreateNewButton from "../commoncomponents/CreateNewButton";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomDialog from "../commoncomponents/CustomDialog";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import PageTitle from "../commoncomponents/PageTitle";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import SectionTitle from "../commoncomponents/SectionTitle";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';
import {  addMonths, addWeeks, format, isBefore, parseISO} from 'date-fns';
export default function PredictiveMaintenanceList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [gridApi, setGridApi] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [serviceOptions, setServiceOptions] = useState();
  const [serviceType, setServiceType] = useState();
  const token = getAuthToken("id_token");
  const companyId = getcompanyId("companyId");
  const facilityId = getFacilityId("facilityId");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [allTracks, setAllTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0);
  const hasRun = useRef(false);
  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "PRMT001");
  const columns = [
    { field: "assetNo", headerName: "Asset No", width: 150 },
    { field: "locationcode", headerName: "Asset Location Code", width: 150 },
    { field: "assetclassification", headerName: "Asset Classification Code", width: 180 },
    { field: "typecode", headerName: "Asset Type Code", width: 180, },
    { field: "typedescription", headerName: "Asset Type Description", width: 150 },
    { field: "taskcode", headerName: "Task Code", width: 150 },
    { field: "assignee", headerName: "Assignee", width: 150 },
    { field: "frequencyunitformate", headerName: "Schedule Frequency", width: 150,  cellStyle: { textAlign: "right" } },
    { field: "intervaltype", headerName: "Interval Type", width: 150 },
    { field: "agreeddateChanged", headerName: "Agreed Date", width: 150, filter:false,sort:false },
    { field: "targetdateChanged", headerName: "Next Date", width: 150, filter:false,sort:false },
    { field: "isactive", headerName: "Status", width: 150 },
  ]

  //To get The all records through API From the database
  const fetchList = async () => {
    try {
      setLoading(true);
      if (serviceType?.label === 'FEMS') {
        const pdmList = await PremitiveMaintenanceRepository.getPDMList(facilityId,filterModel,sortModel,pageIndex,pageSize);
        const list = pdmList?.data?.items?.map((item) => {

         // Calculate next date based on interval type with frequency Unit
          let nextDate;
          const startFromDate = parseISO(item?.agreeddate);
          const currentDate = new Date();

          if (item?.intervaltype === "Weeks") { // Weeks
            nextDate = addWeeks(startFromDate, item?.frequencyunit);
            while (isBefore(nextDate, currentDate)) {
              nextDate = addWeeks(nextDate, item?.frequencyunit);
            }
          } else if (item?.intervaltype === "Months") { // Months
            nextDate = addMonths(startFromDate, item?.frequencyunit);
            while (isBefore(nextDate, currentDate)) {
              nextDate = addMonths(nextDate, item?.frequencyunit);
            }
          }

          const nextPpmDateFormatted = format(nextDate, 'dd/MM/yyyy');

          return {
            ...item,
            agreeddateChanged: format(new Date(item?.agreeddate), "dd/MM/yyyy"),
            targetdateChanged:nextPpmDateFormatted
            // targetdateChanged:format(new Date(item?.targetdate), "dd/MM/yyyy")
          }
        })

        setRows(list)
        setRowCount(pdmList?.data?.totalCount);
        setExportCount(pdmList?.data?.totalCount);
      } else  if (serviceType?.label === 'BEMS'){
        const pdmList = await PremitiveMaintenanceRepository.getBemsPDMList(facilityId,filterModel,sortModel,pageIndex,pageSize);
        const list = pdmList?.data?.items?.map((item) => {
          // Calculate next PPM date based on interval type
          let nextDate;
          const startFromDate = parseISO(item?.agreeddate);
          const currentDate = new Date();

          if (item?.intervaltype === "Weeks") { // Weeks
            nextDate = addWeeks(startFromDate, item?.frequencyunit);
            while (isBefore(nextDate, currentDate)) {
              nextDate = addWeeks(nextDate, item?.frequencyunit);
            }
          } else if (item?.intervaltype === "Months") { // Months
            nextDate = addMonths(startFromDate, item?.frequencyunit);
            while (isBefore(nextDate, currentDate)) {
              nextDate = addMonths(nextDate, item?.frequencyunit);
            }
          }
        
        const nextPpmDateFormatted = format(nextDate, 'dd/MM/yyyy');

          return {
            ...item,
            agreeddateChanged: format(new Date(item?.agreeddate), "dd/MM/yyyy"),
            targetdateChanged:nextPpmDateFormatted
            // targetdateChanged:format(new Date(item?.targetdate), "dd/MM/yyyy")
          }
        })
        setRows(list)
        setRowCount(pdmList?.data?.totalCount);
        setExportCount(pdmList?.data?.totalCount);
      }
    } catch (error) {
      console.error("Error fetching asset categories:", error);
    }
    finally {
      setLoading(false);
    }
  };

  //To get the Export data
  const fetchExportdata = async () => {
  try {  
    if (serviceType?.label === 'FEMS') {
      const pdmList = await PremitiveMaintenanceRepository.getPDMList(facilityId,filterModel,sortModel,pageIndex,pageSize);
      setExportData( pdmList?.data?.items?.map((item) => {

        // Calculate next date based on interval type with frequency Unit
        let nextDate;
        const startFromDate = parseISO(item?.agreeddate);
        const currentDate = new Date();

        if (item?.intervaltype === "Weeks") { // Weeks
          nextDate = addWeeks(startFromDate, item?.frequencyunit);
          while (isBefore(nextDate, currentDate)) {
            nextDate = addWeeks(nextDate, item?.frequencyunit);
          }
        } else if (item?.intervaltype === "Months") { // Months
          nextDate = addMonths(startFromDate, item?.frequencyunit);
          while (isBefore(nextDate, currentDate)) {
            nextDate = addMonths(nextDate, item?.frequencyunit);
          }
        }

        const nextPpmDateFormatted = format(nextDate, 'dd/MM/yyyy');

        return {
          ...item,
          agreeddateChanged: format(new Date(item?.agreeddate), "dd/MM/yyyy"),
          targetdateChanged:nextPpmDateFormatted
          // targetdateChanged:format(new Date(item?.targetdate), "dd/MM/yyyy")
        }
      }));   
    } else  if (serviceType?.label === 'BEMS'){
      const pdmList = await PremitiveMaintenanceRepository.getBemsPDMList(facilityId,filterModel,sortModel,pageIndex,pageSize);
       setExportData(pdmList?.data?.items?.map((item) => {
        // Calculate next date based on interval type with frequency Unit
        let nextDate;
        const startFromDate = parseISO(item?.agreeddate);
        const currentDate = new Date();

        if (item?.intervaltype === "Weeks") { // Weeks
          nextDate = addWeeks(startFromDate, item?.frequencyunit);
          while (isBefore(nextDate, currentDate)) {
            nextDate = addWeeks(nextDate, item?.frequencyunit);
          }
        } else if (item?.intervaltype === "Months") { // Months
          nextDate = addMonths(startFromDate, item?.frequencyunit);
          while (isBefore(nextDate, currentDate)) {
            nextDate = addMonths(nextDate, item?.frequencyunit);
          }
        }

        const nextPpmDateFormatted = format(nextDate, 'dd/MM/yyyy');
        return {
          ...item,
          agreeddateChanged: format(new Date(item?.agreeddate), "dd/MM/yyyy"),
          targetdateChanged:nextPpmDateFormatted
          // targetdateChanged:format(new Date(item?.targetdate), "dd/MM/yyyy")
        }
      })); 
  }
 } catch (error) {
    console.error("Error fetching asset categories:", error);
  }
};
  useEffect(() => {
    fetchList();
}, [filterModel, sortModel, pageIndex, pageSize]);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const responseMasters = await commonMasterRepository.getMasters(facilityId, companyId);
        setServiceOptions(responseMasters);
        setServiceType(responseMasters?.find((item) => item?.label === 'FEMS'));
      } catch (e) {
        console.log(e);
      }
    }
    fetchOptions();
  }, []);
   const serviceChange = useMemo(() => {
    if (serviceType?.label != '' && serviceType?.label != undefined) {
      if (!hasRun.current) {
        fetchList();
        hasRun.current = true;
      }
     } 
   }, [serviceType]);
   
  const handleEdit = (rowData) => {    
    navigate("register", {
      state: { ...rowData, readOnlyEdit: true, mode: "edit", service: serviceType?.label,schYear:rowData?.schyear || 2025 },
    });
  };
  const handleView = (rowData) => {
    navigate("register", {
      state: { ...rowData, readOnly: true, mode: "view", service: serviceType?.label },
    });
  };
  const handleCreatePDM = () => {
    navigate("register", {
      state: { service: serviceType?.label, mode: "create", pdmList: rows },
    });
  };
  const handleDelete = async (row) => {
    if (row?.predictiveId) {
      // const deleteSchedule = serviceType?.label === "FEMS" ? await PremitiveMaintenanceRepository.getPDMShowSchedule(row?.predictiveId) : await PremitiveMaintenanceRepository.getBemsPDMShowSchedule(row?.predictiveId);
      setAllTracks([1]);
    }
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };
  const confirmDelete = async () => {
    try{
    if (rowToDelete) {
      let id = rowToDelete?.predictiveId;

      if (allTracks?.length > 0) {
        if (allTracks?.length > 0) {
          const setTracks = allTracks.map(async (item) => {
            const updatePpmScheduleTracks = serviceType?.label === 'FEMS' ? await PremitiveMaintenanceRepository.removePdmScheduleTracks(id) : await PremitiveMaintenanceRepository.removeBemsPdmScheduleTracks(id);
            return updatePpmScheduleTracks;
          });
          const updatePpmScheduleTracksDelete = await Promise.all(setTracks)
          let isDeleteTracks = updatePpmScheduleTracksDelete.every(result => result.status <= 204);
          if (isDeleteTracks) {
            const response = serviceType?.label === "FEMS" ? await PremitiveMaintenanceRepository.removePdmList(id) : await PremitiveMaintenanceRepository.removeBemsPdmList(id); // Use typecodeId for deletion
            if (response?.status >= 200 && response?.status <= 204)
              setDeleteDialogOpen(false);
            setConfirmationDialogOpen(true);
            setRowToDelete(null); // Clear the row to delete after deletion
            await fetchList();
          }
        }
      }else if(id && allTracks?.length === 0){
        const response = serviceType?.label === "FEMS" ? await PremitiveMaintenanceRepository.removePdmList(id) : await PremitiveMaintenanceRepository.removeBemsPdmList(id); // Use typecodeId for deletion
        if (response?.status >= 200 && response?.status <= 204){
          setDeleteDialogOpen(false);
        setConfirmationDialogOpen(true);
        setRowToDelete(null); // Clear the row to delete after deletion
        await fetchList();
        }
      }
    }}
    catch (e) {
      console.error(e);
    }
  };
  const handleCancelClick = () => {
    navigate('/dashboard');
  }
  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };
  return (
    <>
      <PageTitle title={"Predictive Maintenance"} />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Predictive Maintenance List" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomSelectInput
              fullWidth={false}
              labelSize={"14px"}
              sx={{ width: 150 }}
              label={"Service"}
              value={serviceType}
              options={serviceOptions}
              placeholder={"Select Service"}
              highlight={true}
              onChange={(newValue) => {
                setServiceType(newValue);
                hasRun.current = false;
              }}
            />
           {access?.canCreate && <CreateNewButton onClick={handleCreatePDM} />}
            <RefreshExportButtons
               export={rows}
              exportData={exportData}
             exportFunction={fetchExportdata}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"Predictive Maintenance"}
            />
          </Stack>
        </Box>
        <CustomAgGrid
          rows={rows}
          columns={columns}
          handleEdit={access?.canEdit &&handleEdit}
          handleDelete={access?.canDelete &&handleDelete}
          handleView={access?.canView &&handleView}
          setGridApi={setGridApi}
          isLoading={loading}
          pagination={false}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          headerLayer = {3}
        />
                  <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', margin: "10px" }}>
           <Button variant='outlined' onClick={handleCancelClick} sx={{ margin: "10px" }}>BACK</Button>

            </Grid>
          </Grid>
        <CustomDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}
          title="Confirmation!"
          content="Are you sure you want to delete?"
          cancelBtn="No"
          confirmBtn="Yes"
        />
        <CustomDialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          title="Deletion Successful"
          content="Record deleted successfully."
          confirmBtn="OK"
          onConfirm={() => setConfirmationDialogOpen(false)}
        />
      </DefaultCard>
    </>
  )

}
